<div *transloco="let t" class="p-6">
	<div class="periods-section w-full">
		<div class="section-header flex items-end justify-between gap-2">
			<div class="flex items-center gap-2">
				<mat-icon fontSet="material-symbols-outlined" class="text-primary">settings</mat-icon>

				<h3 class="text-lg font-medium m-0" [ngClass]="{ 'text-gray-400': isDisabled }">
					{{ t('billing.periodSettingsTitle') }} {{ t('common.for') }}
					{{ dateUtil.setDateFormatByActiveLang(periodFc.value?.validFrom, '', false, true) }}
					{{ t('common.until') }}
					{{ dateUtil.setDateFormatByActiveLang(periodFc.value?.validTo, '', false, true) }}
				</h3>
				@if (isDisabled) {
					<mat-icon matTooltip="{{ t('billing.locked') }}" fontSet="material-symbols-outlined">lock</mat-icon>
				}
			</div>

			<mat-form-field appearance="outline" class="w-80 -mb-6">
				<mat-label>{{ t('billing.period') }}</mat-label>
				<mat-select (selectionChange)="loadPeriod($event)" [formControl]="periodFc">
					<mat-select-trigger>
						<!-- custom trigger, if not item will not be displayed -->
						@if (periodFc.value) {
							<div
								class="flex items-center"
								[class.text-gray-400]="periodFc.value?.customerAccessStatus === CustomerAccessStatus.SUBMITTED"
								[class.text-black]="periodFc.value?.customerAccessStatus !== CustomerAccessStatus.SUBMITTED">
								<mat-icon fontSet="material-symbols-outlined" class="mr-2 text-gray-500">
									{{ periodFc.value?.customerAccessStatus === CustomerAccessStatus.SUBMITTED ? 'lock' : 'lock_open_right' }}
								</mat-icon>
								{{ dateUtil.setDateFormatByActiveLang(periodFc.value.validFrom, '', false, true) }}
								- {{ dateUtil.setDateFormatByActiveLang(periodFc.value.validTo, '', false, true) }}
							</div>
						}
					</mat-select-trigger>
					@for (period of periods(); track period.exportId) {
						<mat-option [value]="period">
							<div
								class="flex items-center"
								[class.text-gray-400]="period?.customerAccessStatus === CustomerAccessStatus.SUBMITTED"
								[class.text-black]="period?.customerAccessStatus !== CustomerAccessStatus.SUBMITTED">
								<mat-icon fontSet="material-symbols-outlined" class="mr-2 text-gray-500">
									{{ period?.customerAccessStatus === CustomerAccessStatus.SUBMITTED ? 'lock' : 'lock_open_right' }}
								</mat-icon>
								{{ dateUtil.setDateFormatByActiveLang(period.validFrom, '', false, true) }}
								- {{ dateUtil.setDateFormatByActiveLang(period.validTo, '', false, true) }}
							</div>
						</mat-option>
					}
				</mat-select>
			</mat-form-field>
		</div>
	</div>

	<ng-container>
		<div [@fadeInRight]="animationState()" class="flex flex-col justify-start items-stretch w-full">
			<!-- Allgemeine Einstellungen -->
			<div class="flex flex-row gap-4 w-full px-2">
				<div class="w-1/3 max-w-[350px]">
					<mat-form-field appearance="outline" class="w-full">
						<mat-label>{{ t('billing.regulatoryMode') }}</mat-label>
						<mat-select [formControl]="generalRegulatoryModeFc">
							@for (regulatoryMode of regulatoryModes(); track regulatoryMode) {
								<mat-option [value]="regulatoryMode">
									{{ t('enum.' + regulatoryMode) }}
								</mat-option>
							}
						</mat-select>
					</mat-form-field>
				</div>
				<div class="w-1/3 max-w-[350px]">
					<mat-form-field appearance="outline" class="w-full">
						<mat-label>{{ t('billing.vatType') }}</mat-label>
						<mat-select [formControl]="vatTypeFc">
							@for (vatType of vatTypes(); track vatType) {
								<mat-option [value]="vatType">
									{{ t('vatType.' + vatType) }}
								</mat-option>
							}
						</mat-select>
					</mat-form-field>
				</div>
			</div>
		</div>

		<!-- Deutsche Einstellungen Section -->
		@if (generalRegulatoryModeFc.value === 'GERMAN_STANDARD') {
			<div [@fadeInRight]="animationState()" class="flex flex-col justify-start items-start w-full mt-6">
				<!-- Unterüberschrift für deutsche Einstellungen -->
				<div class="flex items-center justify-start w-full px-2 mb-4">
					<span class="pr-3 flex-none">{{ t('billing.germanSettingsTitle') }}</span>
					<hr class="w-full h-px my-0" />
				</div>

				<!-- Cards Container mit Flexbox -->
				<div class="flex flex-wrap gap-6 w-full px-2" [formGroup]="germanSettingsFg">
					<!-- Card 1: CO2-Kostenaufteilungsgesetz -->
					<div class="flex-1 min-w-[400px] max-w-[650px] flex flex-col rounded-md shadow-lg border bg-gray-50 border-gray-200">
						<div class="px-6 py-4">
							<h4 class="text-base font-medium m-0">
								{{ t('billing.co2CostAllocationActTitle') }}
							</h4>
						</div>
						<div class="p-6 flex-1 flex flex-col gap-4">
							<mat-form-field appearance="outline" class="w-full">
								<mat-label>{{ t('billing.germanRegulatory.buildingLivingSpace') }}</mat-label>
								<input
									matInput
									formControlName="buildingLivingSpace"
									araDecimalFormat
									[allowNegative]="false"
									placeholder="{{ t('common.number') }}" />
								<span matSuffix class="mx-2">m²</span>
							</mat-form-field>

							<mat-form-field appearance="outline" class="w-full">
								<mat-label>{{ t('billing.germanRegulatory.co2OwnerShare') }}</mat-label>
								<input
									matInput
									araDecimalFormat
									[decimalFractionDigitsMax]="0"
									[decimalFractionDigitsMin]="0"
									[allowNegative]="true"
									[minValue]="-100"
									[maxValue]="100"
									min="-100"
									max="100"
									formControlName="co2OwnerShare"
									placeholder="{{ t('common.number') }}" />
								<mat-error>{{ t('common.formInvalid') }}</mat-error>
								<span matSuffix class="mx-2">%</span>
							</mat-form-field>

							<div class="flex flex-col gap-2">
								<mat-checkbox formControlName="envelopeNotImprovable">
									{{ t('billing.germanRegulatory.envelopeNotImprovable') }}
								</mat-checkbox>
								<mat-checkbox formControlName="supplyNotImprovable">
									{{ t('billing.germanRegulatory.supplyNotImprovable') }}
								</mat-checkbox>
								<mat-checkbox formControlName="mainlyCommercial">
									{{ t('billing.germanRegulatory.mainlyCommercial') }}
								</mat-checkbox>
								<mat-checkbox formControlName="heatGridSupplySince23">
									{{ t('billing.germanRegulatory.heatGridSupplySince23') }}
								</mat-checkbox>
								<mat-checkbox formControlName="ownerCooperationBilling">
									{{ t('billing.germanRegulatory.ownerCooperationBilling') }}
								</mat-checkbox>
								<hr />
								<mat-checkbox formControlName="co2SettingsConfirmed">
									{{ t('billing.germanRegulatory.co2SettingsConfirmed') }}
									<span class="mandatory-text">
										{{ t('billing.germanRegulatory.mandatoryInformation') }}
									</span>
								</mat-checkbox>
							</div>
						</div>
					</div>

					<!-- Card 2: IdA – Informationen in der Abrechnung -->
					<div class="flex-1 min-w-[400px] max-w-[650px] flex flex-col rounded-md shadow-lg border bg-gray-50 border-gray-200">
						<div class="px-6 py-4">
							<h4 class="text-base font-medium m-0">
								{{ t('billing.idaInformationTitle') }}
							</h4>
						</div>
						<div class="p-6 flex-1 flex flex-col">
							<mat-checkbox formControlName="renewableEnergy">
								{{ t('billing.germanRegulatory.renewableEnergy') }}
							</mat-checkbox>
						</div>
					</div>
				</div>
			</div>
		}

		<!-- Save Button -->
		@if (!isDisabled) {
			<div class="flex flex-row justify-end items-center w-full mt-6" mat-dialog-actions>
				<span [matTooltip]="!hasChanges() ? t('billing.periodSettingsSaveDisabled') : null">
					<button (click)="save()" class="save-button m-2" color="primary" mat-raised-button [disabled]="!hasChanges()">
						<mat-icon>save</mat-icon>

						@if (!layoutService.isMobile()) {
							<span>{{ t('commonActions.save') }}</span>
						}
					</button>
				</span>
			</div>
		}
	</ng-container>
</div>
